import { useEffect } from 'react';
import { formatDate } from '../utils/dateUtils';
import { linkify } from '../utils/linkify';

// Session AK — the single rendering of "what a staff member sees about an event".
//
// ⚠️ THIS COMPONENT EXISTS TO STOP A CLASS OF BUG, NOT TO SAVE TYPING.
// Session AJ found `transport` displayed on NO staff screen at all, and
// `description` hidden on Next Shift behind an unrelated condition — both for
// months, both invisible to the agent who filled the fields in. The agent
// portal's only view of the data is the EDIT FORM, which shows inputs, not
// output, so nobody could have noticed.
//
// The fix is a preview — but a preview that DUPLICATES this markup would be a
// fourth place to update when a field is added, and it would drift silently.
// **A preview that has drifted is worse than none: it actively reassures the
// agent that information reached staff when it did not.**
//
// So: Book a Shift and BOTH agent previews render THIS component. Add a field
// here and all three gain it at once. ⚠️ Never copy this markup anywhere.
//
// ⚠️ EMPTY FIELDS RENDER AS NOTHING, deliberately. It is tempting to show
// "Transport: not set" in the agent preview — that destroys the entire point,
// which is to show the agent EXACTLY what the staff member sees.
//
// Props:
//   event      the event object, or null/undefined to render nothing.
//              Optional `__seriesLast` (last day) and `__seriesDays` (count)
//              make the header show a date range instead of a single date.
//   onClose    called when the sheet should close.
//   previewNote  true in the agent preview — adds the banner explaining the
//              view. Staff never see it.
export default function EventDetailSheet({ event, onClose, previewNote = false }) {
  // Android hardware back closes the sheet. Scoped tightly: the URL never
  // changes (react-router re-resolves the same route, a no-op), the listener
  // exists only while open, and every close routes through history.back() so
  // there is ONE close path rather than two that can disagree.
  useEffect(() => {
    if (!event) return undefined;
    try { window.history.pushState({ esSheet: true }, ''); } catch (e) { /* no-op */ }
    const onPop = () => onClose();
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // Deps are [event] ONLY, deliberately. `onClose` is a fresh arrow on
    // every parent render, so including it would tear down and re-push a
    // history entry on each render. The captured onClose only ever calls
    // setState, which is stable, so the first one is always correct.
  }, [event]);

  if (!event) return null;

  const close = () => {
    // Only rewind if our own entry is on top, so a failed pushState can never
    // send the person back a real page.
    if (window.history.state && window.history.state.esSheet) window.history.back();
    else onClose();
  };

  return (
    <div
      onClick={ev => { if (ev.target === ev.currentTarget) close(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Event details"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 110 }}
    >
      {/* maxHeight + internal scroll: a long description or a very long event
          name must never push the close control off screen, on any handset. */}
      <div style={{ background: 'var(--bg-card)', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 430, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -4px 30px rgba(0,0,0,0.2)' }}>

        <div style={{ padding: '12px 20px 10px', borderBottom: '1px solid var(--border-light)', flexShrink: 0 }}>
          <div style={{ width: 36, height: 4, background: 'var(--border)', borderRadius: 2, margin: '0 auto 14px' }} />
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 17, color: 'var(--text-primary)', lineHeight: 1.3, overflowWrap: 'anywhere' }}>
                {event.title}
              </div>
              {event.__seriesDays > 1 && (
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginTop: 4 }}>
                  {event.__seriesDays}-day event
                </div>
              )}
            </div>
            <button
              onClick={close}
              aria-label="Close"
              style={{ flexShrink: 0, width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', fontSize: 17, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              ✕
            </button>
          </div>
        </div>

        <div style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px 20px 20px' }}>

          {previewNote && (
            <div style={{ marginBottom: 14, padding: '10px 12px', background: 'rgba(var(--accent-rgb),0.06)', border: '1px solid rgba(var(--accent-rgb),0.25)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              👁 <strong style={{ color: 'var(--text-primary)' }}>This is exactly what your staff see.</strong> Anything you left blank is missing here too — if something is not shown below, they cannot read it.
            </div>
          )}

          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 12 }}>
            <div>
              {event.__seriesLast
                ? `${formatDate(event.date)} – ${formatDate(event.__seriesLast.date)}`
                : formatDate(event.date)}
            </div>
            <div>{event.time_start} – {event.time_end}</div>
          </div>

          {(event.location || event.street_address) && (
            <div style={{ marginBottom: 12, padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Full Address</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
                {[event.location, event.street_address].filter(Boolean).join(', ')}
              </div>
            </div>
          )}

          {event.description && (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 12, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
              {linkify(event.description)}
            </p>
          )}

          {event.transport && (
            <div style={{ marginBottom: 12, padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Transport &amp; Parking</div>
              <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{linkify(event.transport)}</div>
            </div>
          )}

          {event.dress_code && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Dress code:</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{event.dress_code}</span>
            </div>
          )}

          {/* Roles are READ-ONLY here. On Book a Shift, applying happens on the
              card where the Submit bar is visible; a sheet covering that bar
              would hide the only confirmation a selection registered. */}
          {event.roles?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Roles</div>
              {event.roles.map(r => (
                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border-light)' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{r.role}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {r.slots_filled}/{r.spots_available} filled
                  </span>
                </div>
              ))}
            </div>
          )}

          {event.brief_url && (
            <button
              onClick={() => window.open(event.brief_url, '_blank', 'noopener,noreferrer')}
              style={{ width: '100%', fontSize: 13, fontWeight: 700, color: 'var(--accent)', background: 'rgba(var(--accent-rgb),0.07)', border: '1px solid rgba(var(--accent-rgb),0.25)', borderRadius: 8, padding: '9px 14px', cursor: 'pointer', marginBottom: 4 }}
            >
              📄 Read Event Brief
            </button>
          )}

          {(event.location || event.street_address) && (
            <button
              className="btn btn-primary"
              style={{ marginTop: 12 }}
              onClick={() => {
                const address = [event.street_address, event.location].filter(Boolean).join(', ');
                window.open(`https://maps.google.com?q=${encodeURIComponent(address)}`);
              }}
            >
              Get me there →
            </button>
          )}

          <button
            onClick={close}
            style={{ width: '100%', marginTop: 10, padding: '12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            {previewNote ? 'Close preview' : 'Back to shifts'}
          </button>

          <div style={{ height: 'env(safe-area-inset-bottom)' }} />
        </div>
      </div>
    </div>
  );
}
